import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import AppsPage from './pages/AppsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import AdminAppsPage from './pages/AdminAppsPage';
import AdminTerrainPage from './pages/AdminTerrainPage';
import AdminExportImportPage from './pages/AdminExportImportPage';
import AdminOrganizationsPage from './pages/AdminOrganizationsPage';
import AdminOrganizationDetailPage from './pages/AdminOrganizationDetailPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import InvitationPage from './pages/InvitationPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AnalyticsEventsPage from './pages/AnalyticsEventsPage';
import AnalyticsAixmPage from './pages/AnalyticsAixmPage';
import AnalyticsUsersOrganizationsPage from './pages/AnalyticsUsersOrganizationsPage';
import AnalyticsExpiredAccessReportPage from './pages/AnalyticsExpiredAccessReportPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AdminAnnouncementsPage from './pages/AdminAnnouncementsPage';
import { auth } from './lib/auth';
import MainLayout from './components/MainLayout';
import { EnvironmentBar, useApoEnvironment, useEnvironmentTitle } from '@annea/announcements-ui';
import { API_BASE } from './lib/api';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  if (!auth.isAuthenticated()) {
    const returnUrl = window.location.pathname + window.location.search;
    return <Navigate to={`/login?return=${encodeURIComponent(returnUrl)}`} replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function App() {
  // Non-production APO (APO_ENVIRONMENT): a bar above everything and "[DEV] " in the tab title.
  const environment = useApoEnvironment(API_BASE);
  useEnvironmentTitle(environment);
  return (
    <>
      <EnvironmentBar environment={environment} />
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUpPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route path="/invitations/:token" element={<InvitationPage />} />

        {/* Private routes */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route path="/" element={<Navigate to="/apps" replace />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route
            path="/organizations/:id"
            element={<OrganizationDetailPage />}
          />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/analytics/users-organizations" element={<AnalyticsUsersOrganizationsPage />} />
          <Route path="/analytics/expired-access-report" element={<AnalyticsExpiredAccessReportPage />} />
          <Route path="/analytics/events" element={<AnalyticsEventsPage />} />
          <Route path="/analytics/aixm-viewer-light" element={<AnalyticsAixmPage />} />
          <Route path="/admin/apps" element={<AdminAppsPage />} />
          <Route path="/admin/terrain" element={<AdminTerrainPage />} />
          <Route path="/admin/export-import" element={<AdminExportImportPage />} />
          <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
          <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
          <Route
            path="/admin/organizations/:id"
            element={<AdminOrganizationDetailPage />}
          />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
        </Route>


        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
